import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getChatSocket, joinChat, leaveChat } from '@services/realtime.socket';
import {
    CHAT_QUERY_KEY,
    CHAT_THREAD_QUERY_KEY,
    appendMessageToThreadCache,
    mergeMessageIntoThreadCache,
    invalidateThreadCachesForChat,
    appendMessageToChatCache,
} from './useChat';
import { CHAT_LIST_QUERY_KEY } from './useChatList';
import { useAuthStore } from '@stores/auth.store';
import { markChatAsRead as markChatAsReadApi } from '@services/chat.service';

/**
 * Join the chat room (join-chat with ack), listen for server→client events,
 * leave on cleanup (leave-chat with ack).
 *
 * Backend broadcasts chat events to the chat room via emitToChat:
 *   message:new, message:edited, message:deleted, reaction:added, reaction:removed
 */
export function useChatSocket(chatId) {
    const queryClient = useQueryClient();
    const chatIdRef = useRef(chatId);
    chatIdRef.current = chatId;
    const currentUserId = useAuthStore.getState().user?.id ?? null;
    const lastMarkReadAtRef = useRef(0);

    useEffect(() => {
        if (!chatId) return;
        const s = getChatSocket();
        if (!s) return;

        joinChat(chatId);

        const onReconnect = () => {
            joinChat(chatIdRef.current);
        };

        const invalidateChat = () => {
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatIdRef.current) });
        };
        const invalidateChatList = () => {
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        };

        const onMessageNew = (payload) => {
            const message = payload?.message ?? payload;
            const targetChatId = payload?.chatId ?? payload?.chat ?? chatIdRef.current;
            if (message && targetChatId) {
                appendMessageToChatCache(queryClient, targetChatId, message);
                queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                    if (!listData?.items) return listData;
                    return {
                        ...listData,
                        items: listData.items.map((chat) => {
                            const cid = chat.id ?? chat._id;
                            if (String(cid) !== String(targetChatId)) return chat;
                            // If this chat is open, keep unread cleared immediately (prevents "1" flashing/sticking).
                            return {
                                ...chat,
                                lastMessage: message,
                                updatedAt: message.createdAt ?? message.created_at ?? new Date().toISOString(),
                                unreadCount: 0,
                            };
                        }),
                    };
                });

                // If the chat is open and message is from another user, mark as read (throttled).
                const senderId = message?.senderId ?? message?.userId ?? message?.createdBy ?? payload?.senderId ?? null;
                const isFromMe = senderId != null && currentUserId != null && String(senderId) === String(currentUserId);
                const isHidden = typeof document !== 'undefined' ? document.hidden : false;
                if (!isFromMe && !isHidden && String(targetChatId) === String(chatIdRef.current)) {
                    const now = Date.now();
                    if (now - lastMarkReadAtRef.current > 1200) {
                        lastMarkReadAtRef.current = now;
                        // Fire-and-forget; inbox socket will reconcile via chat:read:updated/chat:unread:updated.
                        markChatAsReadApi(chatIdRef.current).catch(() => void 0);
                    }
                }

                const parentId = message?.parentMessageId ?? message?.parent_message_id;
                if (parentId) {
                    const pid = String(parentId);
                    const tKey = CHAT_THREAD_QUERY_KEY(targetChatId, pid);
                    if (queryClient.getQueryData(tKey)) {
                        appendMessageToThreadCache(queryClient, targetChatId, pid, message);
                    }
                }
            }
            // Do not invalidate when payload is empty; rely on send response or backend sending payload
        };

        const onMessageEdited = (payload) => {
            const message = payload?.message ?? payload;
            const targetChatId = payload?.chatId ?? payload?.chat ?? chatIdRef.current;
            const id = message?.id ?? message?._id ?? payload?.messageId ?? null;
            if (!targetChatId || !id) {
                invalidateChat();
                if (targetChatId) invalidateThreadCachesForChat(queryClient, targetChatId);
                return;
            }

            // Merge message update into cache, preserving attachments if omitted in payload.
            queryClient.setQueryData(CHAT_QUERY_KEY(targetChatId), (prev) => {
                if (!prev) return prev;
                const messages = prev.messages ?? {};
                const items = Array.isArray(messages.items) ? messages.items : [];
                const nextItems = items.map((m) => {
                    const mid = m?.id ?? m?._id;
                    if (String(mid) !== String(id)) return m;
                    const prevAttachments = m?.content?.attachments ?? m?.attachments;
                    const next = { ...m, ...message };
                    const nextContent = { ...(m?.content ?? {}), ...(message?.content ?? {}) };
                    const nextAttachmentsField = nextContent?.attachments;
                    if (
                        nextContent &&
                        (nextAttachmentsField == null ||
                            (Array.isArray(nextAttachmentsField) &&
                                nextAttachmentsField.length === 0 &&
                                Array.isArray(prevAttachments) &&
                                prevAttachments.length > 0))
                    ) {
                        nextContent.attachments = prevAttachments;
                    }
                    next.content = nextContent;
                    const topNext = next?.attachments;
                    if (
                        topNext == null ||
                        (Array.isArray(topNext) &&
                            topNext.length === 0 &&
                            Array.isArray(prevAttachments) &&
                            prevAttachments.length > 0)
                    ) {
                        next.attachments = prevAttachments;
                    }
                    return next;
                });
                return { ...prev, messages: { ...messages, items: nextItems } };
            });

            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                return {
                    ...listData,
                    items: listData.items.map((c) => {
                        const cid = c?.id ?? c?._id;
                        if (String(cid) !== String(targetChatId)) return c;
                        const last = c?.lastMessage ?? null;
                        const lid = last?.id ?? last?._id;
                        if (!last || String(lid) !== String(id)) return c;
                        const prevAttachments = last?.content?.attachments ?? last?.attachments;
                        const merged = { ...last, ...message };
                        const mergedContent = { ...(last?.content ?? {}), ...(message?.content ?? {}) };
                        const nextAttachmentsField = mergedContent?.attachments;
                        if (
                            mergedContent &&
                            (nextAttachmentsField == null ||
                                (Array.isArray(nextAttachmentsField) &&
                                    nextAttachmentsField.length === 0 &&
                                    Array.isArray(prevAttachments) &&
                                    prevAttachments.length > 0))
                        ) {
                            mergedContent.attachments = prevAttachments;
                        }
                        merged.content = mergedContent;
                        const topNext = merged?.attachments;
                        if (
                            topNext == null ||
                            (Array.isArray(topNext) &&
                                topNext.length === 0 &&
                                Array.isArray(prevAttachments) &&
                                prevAttachments.length > 0)
                        ) {
                            merged.attachments = prevAttachments;
                        }
                        return { ...c, lastMessage: merged };
                    }),
                };
            });

            const parentId = message?.parentMessageId ?? message?.parent_message_id;
            if (parentId) {
                mergeMessageIntoThreadCache(queryClient, targetChatId, String(parentId), message);
            }
        };

        const onMessageDeleted = () => {
            invalidateChat();
            invalidateChatList();
            invalidateThreadCachesForChat(queryClient, chatIdRef.current);
        };

        const onReactionAdded = (payload) => {
            // Keep local cache accurate and allow "my reaction" highlighting even when API doesn't return userIds per reaction.
            const { messageId, userId, emoji, chatId: payloadChatId } = payload ?? {};
            const targetChatId = payloadChatId ?? chatIdRef.current;
            if (!messageId || !targetChatId) {
                invalidateChat();
                return;
            }
            if (!currentUserId || String(userId) !== String(currentUserId)) {
                invalidateChat();
                invalidateThreadCachesForChat(queryClient, targetChatId);
                return;
            }

            queryClient.setQueryData(CHAT_QUERY_KEY(targetChatId), (prev) => {
                if (!prev) return prev;
                const messages = prev.messages ?? {};
                const items = Array.isArray(messages.items) ? messages.items : [];
                const nextItems = items.map((m) => {
                    const mid = m.id ?? m._id;
                    if (String(mid) !== String(messageId)) return m;
                    return { ...m, myReactionEmoji: emoji };
                });
                return { ...prev, messages: { ...messages, items: nextItems } };
            });
            invalidateThreadCachesForChat(queryClient, targetChatId);
        };

        const onReactionRemoved = (payload) => {
            const { messageId, userId, chatId: payloadChatId } = payload ?? {};
            const targetChatId = payloadChatId ?? chatIdRef.current;
            if (!messageId || !targetChatId) {
                invalidateChat();
                return;
            }
            if (!currentUserId || String(userId) !== String(currentUserId)) {
                invalidateChat();
                invalidateThreadCachesForChat(queryClient, targetChatId);
                return;
            }

            queryClient.setQueryData(CHAT_QUERY_KEY(targetChatId), (prev) => {
                if (!prev) return prev;
                const messages = prev.messages ?? {};
                const items = Array.isArray(messages.items) ? messages.items : [];
                const nextItems = items.map((m) => {
                    const mid = m.id ?? m._id;
                    if (String(mid) !== String(messageId)) return m;
                    return { ...m, myReactionEmoji: null };
                });
                return { ...prev, messages: { ...messages, items: nextItems } };
            });
            invalidateThreadCachesForChat(queryClient, targetChatId);
        };

        // Group metadata events can be emitted to the chat room (emitToChat),
        // so we also listen here (in addition to inbox-level listeners).
        const onGroupMetaChanged = (payload) => {
            const targetChatId = payload?.chatId ?? payload?.id ?? chatIdRef.current;
            if (!targetChatId) return;
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(targetChatId) });
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        };

        s.on('connect', onReconnect);
        s.on('message:new', onMessageNew);
        s.on('message:edited', onMessageEdited);
        s.on('message:deleted', onMessageDeleted);
        s.on('reaction:added', onReactionAdded);
        s.on('reaction:removed', onReactionRemoved);
        s.on('chat:cleared', onGroupMetaChanged);
        s.on('chat:group:renamed', onGroupMetaChanged);
        s.on('chat:group:members:added', onGroupMetaChanged);
        s.on('chat:group:member:removed', onGroupMetaChanged);
        s.on('chat:group:member:left', onGroupMetaChanged);
        s.on('chat:group:exited', onGroupMetaChanged);
        s.on('chat:group:admin:change', onGroupMetaChanged);
        s.on('chat:group:admin:assigned', onGroupMetaChanged);

        return () => {
            leaveChat(chatId);
            s.off('connect', onReconnect);
            s.off('message:new', onMessageNew);
            s.off('message:edited', onMessageEdited);
            s.off('message:deleted', onMessageDeleted);
            s.off('reaction:added', onReactionAdded);
            s.off('reaction:removed', onReactionRemoved);
            s.off('chat:cleared', onGroupMetaChanged);
            s.off('chat:group:renamed', onGroupMetaChanged);
            s.off('chat:group:members:added', onGroupMetaChanged);
            s.off('chat:group:member:removed', onGroupMetaChanged);
            s.off('chat:group:member:left', onGroupMetaChanged);
            s.off('chat:group:exited', onGroupMetaChanged);
            s.off('chat:group:admin:change', onGroupMetaChanged);
            s.off('chat:group:admin:assigned', onGroupMetaChanged);
        };
    }, [chatId, queryClient]);
}
